import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';

import App from './App.tsx';
import { ReactQueryDevtools } from '@/app/debug/ReactQueryDevtools';
import { initSentry } from '@/app/monitoring/initSentry';
import { registerPwa } from '@/app/pwa/registerPwa';
import { AppErrorBoundary } from '@/shared/ui/app-error-boundary/AppErrorBoundary.tsx';
import { Notifications } from '@/components/notification/Notifications.tsx';
import i18n from '../src/i18n';
import customTheme from './theme';

import './index.css';
import './fonts.css';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

// https://vite.dev/guide/build#load-error-handling
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

initSentry();

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onError: (_error, _query) => {
      // console.debug(error, query);
    },
  }),
  mutationCache: new MutationCache({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onError: (_error, _variables, _context, _mutation) => {
      // console.debug(error, variables, context, mutation);
    },
  }),
});

const rootElement = document.getElementById('root')!;
ReactDOM.createRoot(rootElement, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  // <React.StrictMode>
  <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={customTheme}>
      <CssBaseline enableColorScheme />
      <I18nextProvider i18n={i18n}>
        <AppErrorBoundary boundaryId="application" onReset={() => window.location.reload()}>
          <Notifications />
          <App />
        </AppErrorBoundary>
      </I18nextProvider>
      {import.meta.env.DEV && <ReactQueryDevtools />}
    </ThemeProvider>
  </QueryClientProvider>
  // </React.StrictMode>
);

registerPwa(rootElement);
